/* ============================================================
 * AYU /buch/danke – Bestätigungsseite nach Stripe-Zahlung
 * 
 * Flow:
 * 1. Käufer kommt von Stripe mit ?lang=de oder ?lang=en
 * 2. Gibt Name, E-Mail, Telefon, Adresse ein
 * 3. Erhält zeitlich begrenzten Download-Token (48h, max 3 Downloads)
 * 4. PDF wird mit personalisiertem Wasserzeichen versehen
 * 
 * Success-URLs für Stripe:
 * DE: https://ayuhealth-atzyym4v.manus.space/buch/danke?lang=de
 * EN: https://ayuhealth-atzyym4v.manus.space/buch/danke?lang=en
 * ============================================================ */
import { useState, useEffect } from "react";
import { Link } from "wouter";
import { IMAGES } from "@/lib/constants";
import {
  Download, CheckCircle, BookOpen, Heart,
  ArrowRight, Mail, Sparkles, ShieldCheck,
  Clock, AlertTriangle, Loader2, User,
  Phone, MapPin, Lock
} from "lucide-react";

const BOOK_COVERS = {
  de: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/buch-cover-de_083abd69.png",
  en: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/buch-cover-en_e18a1b95.png",
};

type Step = "form" | "ready" | "expired" | "maxReached";

export default function BuchDanke() {
  // Get lang from URL params
  const params = new URLSearchParams(window.location.search);
  const langParam = params.get("lang");
  const lang: "de" | "en" = langParam === "en" ? "en" : "de";

  // Check if we have a stored token
  const storedToken = localStorage.getItem(`ebook_token_${lang}`);

  const [step, setStep] = useState<Step>(storedToken ? "ready" : "form");
  const [token, setToken] = useState(storedToken || "");
  const [downloadsRemaining, setDownloadsRemaining] = useState(3);
  const [expiresAt, setExpiresAt] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState("");

  // Form fields
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formAddress, setFormAddress] = useState("");

  // Verify stored token on mount
  useEffect(() => {
    if (storedToken) {
      verifyToken(storedToken);
    }
  }, [storedToken]);

  async function verifyToken(t: string) {
    try {
      const res = await fetch(`/api/ebook/verify/${t}`);
      const data = await res.json();
      if (data.valid) {
        setStep("ready");
        setDownloadsRemaining(data.downloadsRemaining);
        setExpiresAt(data.expiresAt);
        setBuyerName(data.buyerName);
      } else if (data.expired) {
        setStep("expired");
        localStorage.removeItem(`ebook_token_${lang}`);
      } else if (data.maxReached) {
        setStep("maxReached");
      } else {
        // Token invalid, show form again
        setStep("form");
        localStorage.removeItem(`ebook_token_${lang}`);
      }
    } catch {
      setStep("form");
      localStorage.removeItem(`ebook_token_${lang}`);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const res = await fetch("/api/ebook/create-download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formName,
          email: formEmail,
          phone: formPhone,
          address: formAddress,
          lang,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setToken(data.token);
        setDownloadsRemaining(data.maxDownloads);
        setExpiresAt(data.expiresAt);
        setBuyerName(formName);
        setStep("ready");
        // Store token for page refresh
        localStorage.setItem(`ebook_token_${lang}`, data.token);
      } else {
        setError(data.error || "Ein Fehler ist aufgetreten.");
      }
    } catch {
      setError("Verbindungsfehler. Bitte versuche es erneut.");
    } finally {
      setIsLoading(false);
    }
  }

  function handleDownload() {
    if (!token) return;
    setIsDownloading(true);
    // Open download in new tab / trigger download
    window.open(`/api/ebook/download/${token}`, "_blank");
    // Decrement local counter
    setTimeout(() => {
      setDownloadsRemaining((prev) => Math.max(0, prev - 1));
      setIsDownloading(false);
      if (downloadsRemaining <= 1) {
        setStep("maxReached");
      }
    }, 2000);
  }

  function formatExpiry(iso: string) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[oklch(0.12_0.02_65)] via-[oklch(0.15_0.02_65)] to-[oklch(0.98_0.005_85)]">

      {/* ── Nav ── */}
      <nav className="fixed top-0 w-full z-50 bg-[oklch(0.12_0.02_65)/0.95] backdrop-blur-md border-b border-gold/10">
        <div className="container max-w-6xl mx-auto flex items-center justify-between h-16 px-4">
          <Link href="/" className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="AYU" className="h-8 w-8" />
            <span className="font-playfair text-lg text-[oklch(0.92_0.02_85)]">AYU</span>
          </Link>
          <Link href="/buch" className="text-sm text-[oklch(0.7_0.02_85)] hover:text-gold transition-colors">
            Zur Buchseite
          </Link>
        </div>
      </nav>

      {/* ========== DANKE HERO ========== */}
      <section className="pt-28 pb-10">
        <div className="container max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-olive/15 mb-8">
            <CheckCircle className="w-10 h-10 text-olive-light" />
          </div>

          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-[oklch(0.92_0.02_85)] mb-4">
            Vielen Dank f&uuml;r deinen <span className="italic text-gold">Kauf!</span>
          </h1>

          <p className="text-lg text-[oklch(0.65_0.02_85)] mb-2 max-w-xl mx-auto">
            {lang === "de"
              ? "Dein E-Book \"Der AYU\" steht zum Download bereit."
              : "Your e-book \"The AYU\" is ready for download."}
          </p>

          {/* Language badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold/10 border border-gold/20 mt-4">
            <BookOpen className="w-4 h-4 text-gold" />
            <span className="text-sm font-semibold text-gold">
              {lang === "de" ? "Deutsche Ausgabe" : "English Edition"}
            </span>
          </div>
        </div>
      </section>

      {/* ========== MAIN CONTENT ========== */}
      <section className="pb-20">
        <div className="container max-w-2xl mx-auto px-4">
          <div className="bg-[oklch(0.18_0.02_65)] rounded-3xl p-8 md:p-12 border border-gold/15 shadow-2xl">

            {/* Book Cover Preview */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="absolute -inset-4 bg-gold/10 rounded-2xl blur-2xl" />
                <img
                  src={BOOK_COVERS[lang]}
                  alt={lang === "de" ? "Der AYU - Cover" : "The AYU - Cover"}
                  className="relative w-40 rounded-lg shadow-xl"
                />
              </div>
            </div>

            {/* ── STEP: FORM ── */}
            {step === "form" && (
              <div>
                <div className="text-center mb-6">
                  <h2 className="font-playfair text-xl font-bold text-[oklch(0.92_0.02_85)] mb-2">
                    {lang === "de" ? "Deine Daten für den Download" : "Your details for download"}
                  </h2>
                  <p className="text-sm text-[oklch(0.55_0.02_85)]">
                    {lang === "de"
                      ? "Dein E-Book wird personalisiert und ist nur für dich bestimmt."
                      : "Your e-book will be personalized and is for your use only."}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-[oklch(0.75_0.02_85)] mb-1.5">
                      <User className="w-4 h-4" />
                      {lang === "de" ? "Vollständiger Name *" : "Full Name *"}
                    </label>
                    <input
                      type="text"
                      required
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder={lang === "de" ? "Max Mustermann" : "John Doe"}
                      className="w-full px-4 py-3 rounded-xl bg-[oklch(0.14_0.02_65)] border border-gold/15 text-[oklch(0.9_0.02_85)] placeholder:text-[oklch(0.4_0.02_85)] focus:outline-none focus:border-gold/40 transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-[oklch(0.75_0.02_85)] mb-1.5">
                      <Mail className="w-4 h-4" />
                      {lang === "de" ? "E-Mail-Adresse *" : "Email Address *"}
                    </label>
                    <input
                      type="email"
                      required
                      value={formEmail}
                      onChange={(e) => setFormEmail(e.target.value)}
                      placeholder="name@example.com"
                      className="w-full px-4 py-3 rounded-xl bg-[oklch(0.14_0.02_65)] border border-gold/15 text-[oklch(0.9_0.02_85)] placeholder:text-[oklch(0.4_0.02_85)] focus:outline-none focus:border-gold/40 transition-colors"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-[oklch(0.75_0.02_85)] mb-1.5">
                      <Phone className="w-4 h-4" />
                      {lang === "de" ? "Telefonnummer" : "Phone Number"}
                    </label>
                    <input
                      type="tel"
                      value={formPhone}
                      onChange={(e) => setFormPhone(e.target.value)}
                      placeholder="+49 123 456 7890"
                      className="w-full px-4 py-3 rounded-xl bg-[oklch(0.14_0.02_65)] border border-gold/15 text-sm sm:text-base text-[oklch(0.9_0.02_85)] placeholder:text-[oklch(0.4_0.02_85)] focus:outline-none focus:border-gold/40 transition-colors"
                    />
                  </div>

                  {/* Address */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-[oklch(0.75_0.02_85)] mb-1.5">
                      <MapPin className="w-4 h-4" />
                      {lang === "de" ? "Adresse" : "Address"}
                    </label>
                    <textarea
                      value={formAddress}
                      onChange={(e) => setFormAddress(e.target.value)}
                      placeholder={lang === "de" ? "Straße, PLZ, Ort, Land" : "Street, Zip, City, Country"}
                      rows={2}
                      className="w-full px-4 py-3 rounded-xl bg-[oklch(0.14_0.02_65)] border border-gold/15 text-[oklch(0.9_0.02_85)] placeholder:text-[oklch(0.4_0.02_85)] focus:outline-none focus:border-gold/40 transition-colors resize-none"
                    />
                  </div>

                  {/* Info box */}
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-gold/5 border border-gold/10">
                    <Lock className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-[oklch(0.6_0.02_85)] leading-relaxed">
                      {lang === "de"
                        ? "Deine Daten werden ausschließlich zur Personalisierung deines E-Books verwendet. Das PDF wird mit einem Wasserzeichen versehen, um unerlaubte Weitergabe zu verhindern. Du hast 48 Stunden und maximal 3 Downloads."
                        : "Your data is used exclusively to personalize your e-book. The PDF will be watermarked to prevent unauthorized sharing. You have 48 hours and a maximum of 3 downloads."}
                    </p>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex items-center justify-center gap-3 w-full py-4 rounded-full bg-gold text-[oklch(0.15_0.02_65)] font-bold text-lg hover:bg-gold-light transition-all hover:scale-[1.02] shadow-lg disabled:opacity-60 disabled:hover:scale-100"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {lang === "de" ? "Wird vorbereitet..." : "Preparing..."}
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5" />
                        {lang === "de" ? "Download freischalten" : "Unlock Download"}
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* ── STEP: READY ── */}
            {step === "ready" && (
              <div>
                <div className="text-center mb-6">
                  <h2 className="font-playfair text-xl font-bold text-[oklch(0.92_0.02_85)] mb-2">
                    {lang === "de"
                      ? `Hallo ${buyerName}, dein E-Book ist bereit!`
                      : `Hello ${buyerName}, your e-book is ready!`}
                  </h2>
                </div>

                {/* Download info */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-[oklch(0.14_0.02_65)] border border-gold/10">
                    <Download className="w-4 h-4 text-gold" />
                    <div>
                      <p className="text-xs text-[oklch(0.5_0.02_85)]">
                        {lang === "de" ? "Verbleibend" : "Remaining"}
                      </p>
                      <p className="text-sm font-bold text-[oklch(0.9_0.02_85)]">
                        {downloadsRemaining} / 3 Downloads
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-[oklch(0.14_0.02_65)] border border-gold/10">
                    <Clock className="w-4 h-4 text-gold" />
                    <div>
                      <p className="text-xs text-[oklch(0.5_0.02_85)]">
                        {lang === "de" ? "Gültig bis" : "Valid until"}
                      </p>
                      <p className="text-sm font-bold text-[oklch(0.9_0.02_85)]">
                        {formatExpiry(expiresAt)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Watermark notice */}
                <div className="flex items-start gap-3 p-4 rounded-xl bg-gold/5 border border-gold/10 mb-6">
                  <ShieldCheck className="w-5 h-5 text-olive-light flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-[oklch(0.6_0.02_85)] leading-relaxed">
                    {lang === "de"
                      ? "Dein PDF ist personalisiert und enthält ein Wasserzeichen mit deinen Daten. Die Vervielfältigung oder Weitergabe ist untersagt und wird rechtlich verfolgt."
                      : "Your PDF is personalized and contains a watermark with your details. Reproduction or redistribution is prohibited and will be prosecuted."}
                  </p>
                </div>

                {/* Download Button */}
                <button
                  onClick={handleDownload}
                  disabled={isDownloading || downloadsRemaining <= 0}
                  className="flex items-center justify-center gap-3 w-full py-4 rounded-full bg-gold text-[oklch(0.15_0.02_65)] font-bold text-lg hover:bg-gold-light transition-all hover:scale-[1.02] shadow-lg disabled:opacity-60 disabled:hover:scale-100"
                >
                  {isDownloading ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin" />
                      {lang === "de" ? "PDF wird erstellt..." : "Creating PDF..."}
                    </>
                  ) : (
                    <>
                      <Download className="w-6 h-6" />
                      {lang === "de" ? "E-Book herunterladen (PDF)" : "Download E-Book (PDF)"}
                    </>
                  )}
                </button>

                <p className="text-center text-xs text-[oklch(0.45_0.02_85)] mt-3">
                  {lang === "de"
                    ? "Das personalisierte PDF wird beim Download erstellt. Dies kann einige Sekunden dauern."
                    : "The personalized PDF is created on download. This may take a few seconds."}
                </p>
              </div>
            )}

            {/* ── STEP: EXPIRED ── */}
            {step === "expired" && (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 mb-6">
                  <Clock className="w-8 h-8 text-amber-400" />
                </div>
                <h2 className="font-playfair text-xl font-bold text-[oklch(0.92_0.02_85)] mb-3">
                  {lang === "de" ? "Download-Link abgelaufen" : "Download link expired"}
                </h2>
                <p className="text-sm text-[oklch(0.6_0.02_85)] mb-6">
                  {lang === "de"
                    ? "Dein Download-Link ist nach 48 Stunden abgelaufen. Bitte kontaktiere uns, um einen neuen Link zu erhalten."
                    : "Your download link has expired after 48 hours. Please contact us to receive a new link."}
                </p>
                <a
                  href="mailto:info@ayu-rveda.com?subject=E-Book Download Link abgelaufen"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-[oklch(0.15_0.02_65)] font-semibold hover:bg-gold-light transition-all"
                >
                  <Mail className="w-4 h-4" />
                  {lang === "de" ? "Kontakt aufnehmen" : "Contact us"}
                </a>
              </div>
            )}

            {/* ── STEP: MAX REACHED ── */}
            {step === "maxReached" && (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 mb-6">
                  <AlertTriangle className="w-8 h-8 text-amber-400" />
                </div>
                <h2 className="font-playfair text-xl font-bold text-[oklch(0.92_0.02_85)] mb-3">
                  {lang === "de" ? "Download-Limit erreicht" : "Download limit reached"}
                </h2>
                <p className="text-sm text-[oklch(0.6_0.02_85)] mb-6">
                  {lang === "de"
                    ? "Du hast die maximale Anzahl von 3 Downloads erreicht. Bitte kontaktiere uns, falls du weitere Downloads benötigst."
                    : "You have reached the maximum of 3 downloads. Please contact us if you need additional downloads."}
                </p>
                <a
                  href="mailto:info@ayu-rveda.com?subject=E-Book weitere Downloads"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-[oklch(0.15_0.02_65)] font-semibold hover:bg-gold-light transition-all"
                >
                  <Mail className="w-4 h-4" />
                  {lang === "de" ? "Kontakt aufnehmen" : "Contact us"}
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ========== NEXT STEPS ========== */}
      <section className="py-16 bg-[oklch(0.98_0.005_85)]">
        <div className="container max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-olive mb-3">
              {lang === "de" ? "Und jetzt?" : "What's next?"}
            </p>
            <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[oklch(0.2_0.02_65)]">
              {lang === "de"
                ? <>Deine n&auml;chsten <span className="italic text-olive">Schritte</span></>
                : <>Your next <span className="italic text-olive">steps</span></>}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: BookOpen,
                title: lang === "de" ? "Buch lesen" : "Read the book",
                desc: lang === "de"
                  ? "Tauche ein in die Welt von AYU und entdecke, wie Ayurveda in Europa wirklich funktioniert."
                  : "Dive into the world of AYU and discover how Ayurveda really works in Europe.",
                color: "olive",
              },
              {
                icon: Sparkles,
                title: lang === "de" ? "Deinen Typ entdecken" : "Discover your type",
                desc: lang === "de"
                  ? "Finde heraus, welcher der 12 funktionellen Menschentypen du bist."
                  : "Find out which of the 12 functional human types you are.",
                link: "/funnel",
                linkText: lang === "de" ? "Zum Erlebnis" : "Start experience",
                color: "gold",
              },
              {
                icon: Heart,
                title: lang === "de" ? "Beratung buchen" : "Book a consultation",
                desc: lang === "de"
                  ? "Lass dich kostenlos und unverbindlich von unserem Team beraten."
                  : "Get a free, no-obligation consultation from our team.",
                link: "/termin",
                linkText: lang === "de" ? "Termin vereinbaren" : "Schedule appointment",
                color: "olive",
              },
            ].map((step, i) => (
              <div
                key={i}
                className="p-6 rounded-2xl bg-white border border-[oklch(0.9_0.01_85)] hover:shadow-lg transition-all"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  step.color === "gold" ? "bg-gold/10" : "bg-olive/10"
                }`}>
                  <step.icon className={`w-6 h-6 ${step.color === "gold" ? "text-gold" : "text-olive"}`} />
                </div>
                <h3 className="font-semibold text-[oklch(0.2_0.02_65)] mb-2">{step.title}</h3>
                <p className="text-sm text-[oklch(0.45_0.02_65)] mb-4">{step.desc}</p>
                {step.link && (
                  <Link
                    href={step.link}
                    className={`inline-flex items-center gap-1 text-sm font-semibold transition-colors ${
                      step.color === "gold" ? "text-gold-dark hover:text-gold" : "text-olive hover:text-olive-dark"
                    }`}
                  >
                    {step.linkText} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-8 bg-[oklch(0.12_0.02_65)] border-t border-gold/10">
        <div className="container max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={IMAGES.logo} alt="AYU" className="h-6 w-6" />
            <span className="text-sm text-[oklch(0.5_0.02_85)]">&copy; 2025 AYU-rveda. Alle Rechte vorbehalten.</span>
          </div>
          <div className="flex gap-6 text-sm text-[oklch(0.5_0.02_85)]">
            <Link href="/" className="hover:text-gold transition-colors">Startseite</Link>
            <Link href="/buch" className="hover:text-gold transition-colors">Buch</Link>
            <Link href="/termin" className="hover:text-gold transition-colors">Beratung</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
